"use client";
import { useMemo, useEffect, useState } from 'react';
import { cn } from '../lib/utils';

type ProgressRingProps = {
    progress: number; // 0-100
    size?: number;
    strokeWidth?: number;
    className?: string;
    showLabel?: boolean;
    variant?: 'default' | 'spiral';
    completed?: boolean;
};

export function ProgressRing({ 
    progress, 
    size = 64, 
    strokeWidth = 6, 
    className, 
    showLabel = true,
    variant = 'default',
    completed = false
}: ProgressRingProps) {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (progress / 100) * circumference;
    const [shouldPulse, setShouldPulse] = useState(false);

    const displayText = useMemo(() => {
        return `${Math.round(progress)}%`;
    }, [progress]);

    // Trigger completion pulse when progress reaches 100%
    useEffect(() => {
        if (progress >= 100 && completed) {
            setShouldPulse(true);
            const timer = setTimeout(() => setShouldPulse(false), 600);
            return () => clearTimeout(timer);
        }
    }, [progress, completed]);

    // Color gradient from cool to warm as progress increases
    const progressColor = useMemo(() => {
        if (progress >= 100) return 'text-[hsl(var(--virtue-gold))]';
        if (progress >= 75) return 'text-primary';
        if (progress >= 50) return 'text-primary opacity-90';
        return 'text-primary opacity-75';
    }, [progress]);

    return (
        <div 
            className={cn(
                'relative inline-flex items-center justify-center',
                shouldPulse && 'animate-completion-pulse',
                className
            )} 
            style={{ width: size, height: size }}
        >
            <svg width={size} height={size} className="transform -rotate-90">
                {/* Background ring */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="none"
                    className="text-muted opacity-20"
                />
                {/* Progress ring with smooth animation */}
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="currentColor"
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    className={cn(
                        progressColor,
                        'transition-all duration-500 ease-[cubic-bezier(0.4,0,0.2,1)]'
                    )}
                    style={{ 
                        strokeDashoffset: offset,
                        '--circumference': circumference 
                    } as React.CSSProperties & { '--circumference': number }}
                />
            </svg>
            {showLabel && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className={cn(
                        "text-xs font-semibold tabular-nums",
                        progress >= 100 && "virtue-gold"
                    )}>{displayText}</span>
                </div>
            )}
        </div>
    );
}

